import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchableSelect from './SearchableSelect';
import { classOptions, classOptionsWithEmpty, classOptionsWithAll, ALL_CLASSES_VALUE, CLASSES, formatClassLabel } from '../constants/classes';
import {
  listSubjects,
  saveSubject,
  updateSubject,
  deleteSubject,
  listAssessments,
  saveAssessment,
  deleteAssessment,
  listAllSyllabus,
  saveSyllabus,
  subjectsForClass,
  subjectAppliesToClass,
} from '../api/academicsApi';
import { prepareTeacherSeeds } from '../api/adminApi';
import { DEFAULT_TEACHERS } from '../constants/teachers';
import { parseSyllabusCsv, downloadSyllabusPdf } from '../utils/syllabusPdf';
import { matchesSearch } from '../utils/searchFilter';

export default function AdminAcademicsTab({ setMsg, setErr }) {
  const [subjects, setSubjects] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [syllabusRows, setSyllabusRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seedingTeachers, setSeedingTeachers] = useState(false);

  const [subjectForm, setSubjectForm] = useState({ id: '', name: '', className: '' });
  const [editingSubjectId, setEditingSubjectId] = useState('');
  const [assessmentForm, setAssessmentForm] = useState({
    name: '',
    type: 'EXAM',
    className: '1',
    selectedSubjects: [],
    maxMarks: '100',
    date: new Date().toISOString().slice(0, 10),
  });
  const [pdfClass, setPdfClass] = useState('1');
  const [filter, setFilter] = useState('');

  const classOpts = useMemo(() => classOptions(), []);
  const classOptsAll = useMemo(() => classOptionsWithAll(), []);
  const classOptsEmpty = useMemo(() => classOptionsWithEmpty('All classes'), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, y] = await Promise.all([listSubjects(), listAssessments(), listAllSyllabus()]);
      setSubjects(s);
      setAssessments(a);
      setSyllabusRows(y);
    } catch (e) {
      setErr(e.message || 'Failed to load academics data');
    } finally {
      setLoading(false);
    }
  }, [setErr]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubject = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    try {
      if (editingSubjectId) {
        await updateSubject({ id: editingSubjectId, name: subjectForm.name, className: subjectForm.className });
        setMsg(`Subject updated: ${subjectForm.name}`);
      } else {
        await saveSubject(subjectForm);
        setMsg(`Subject saved: ${subjectForm.name}`);
      }
      setSubjectForm({ id: '', name: '', className: '' });
      setEditingSubjectId('');
      await load();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const handleEditSubject = (row) => {
    setEditingSubjectId(row.id);
    setSubjectForm({ id: row.id, name: row.name, className: row.class || '' });
  };

  const handleCancelEditSubject = () => {
    setEditingSubjectId('');
    setSubjectForm({ id: '', name: '', className: '' });
  };

  const handleDeleteSubject = async (row) => {
    if (!window.confirm(`Remove subject "${row.name}"?`)) return;
    setMsg('');
    setErr('');
    try {
      await deleteSubject(row.id);
      if (editingSubjectId === row.id) handleCancelEditSubject();
      setMsg(`Subject removed: ${row.name}`);
      await load();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const handleAssessment = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    if (!assessmentForm.selectedSubjects?.length) {
      setErr('Please select at least one subject.');
      return;
    }
    try {
      const isAllClasses = assessmentForm.className === ALL_CLASSES_VALUE;
      const targetClasses = isAllClasses ? CLASSES : [assessmentForm.className];
      let count = 0;
      for (const cls of targetClasses) {
        for (const subjectName of assessmentForm.selectedSubjects) {
          const sub = subjects.find((s) => s.name === subjectName);
          if (sub && !subjectAppliesToClass(sub, cls)) continue;
          await saveAssessment({ ...assessmentForm, className: cls, subject: subjectName });
          count += 1;
        }
      }
      const classNote = isAllClasses ? `all classes (${targetClasses.length})` : `class ${formatClassLabel(assessmentForm.className)}`;
      setMsg(
        `${assessmentForm.type === 'TEST' ? 'Test' : 'Exam'} "${assessmentForm.name}" saved — ${count} record(s) across ${classNote}.`
      );
      setAssessmentForm((f) => ({ ...f, name: '', selectedSubjects: [] }));
      await load();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const handleDeleteAssessment = async (row) => {
    if (!window.confirm(`Delete ${row.type} "${row.name}"?`)) return;
    setMsg('');
    setErr('');
    try {
      await deleteAssessment(row.id);
      setMsg('Assessment deleted.');
      await load();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const handleSyllabusImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMsg('');
    setErr('');
    try {
      const rows = parseSyllabusCsv(await file.text());
      if (!rows.length) throw new Error('No valid rows. CSV format: class,subject,content');
      for (const row of rows) {
        await saveSyllabus(row);
      }
      setMsg(`Imported ${rows.length} syllabus row(s).`);
      await load();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const handlePdf = () => {
    const rows = syllabusRows.filter((r) => r.class === pdfClass || (pdfClass === 'L.K.G' && r.class === 'LKG'));
    downloadSyllabusPdf(pdfClass, rows);
  };

  const handleSeedTeachers = async () => {
    setMsg('');
    setErr('');
    setSeedingTeachers(true);
    try {
      const emails = await prepareTeacherSeeds(DEFAULT_TEACHERS.map((t) => ({ ...t, allClasses: true })));
      setMsg(
        `Teacher profiles prepared for ${emails.length} teachers. Run scripts/import-teachers.js to create login passwords, then share credentials.`
      );
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSeedingTeachers(false);
    }
  };

  const filteredAssessments = useMemo(
    () =>
      assessments.filter((a) =>
        matchesSearch(filter, [a.name, a.type, a.class, a.subject, a.date, String(a.maxMarks)])
      ),
    [assessments, filter]
  );

  const classSubjectRows = useMemo(
    () => subjectsForClass(subjects, assessmentForm.className),
    [subjects, assessmentForm.className]
  );

  const toggleAssessmentSubject = (name) => {
    setAssessmentForm((f) => {
      const set = new Set(f.selectedSubjects || []);
      if (set.has(name)) set.delete(name);
      else set.add(name);
      return { ...f, selectedSubjects: [...set] };
    });
  };

  const selectAllAssessmentSubjects = () => {
    setAssessmentForm((f) => ({
      ...f,
      selectedSubjects: classSubjectRows.map((s) => s.name),
    }));
  };

  const clearAssessmentSubjects = () => {
    setAssessmentForm((f) => ({ ...f, selectedSubjects: [] }));
  };

  return (
    <>
      <div className="mps-card">
        <h2>Default teachers (all classes)</h2>
        <p className="mps-muted" style={{ marginTop: 0 }}>
          Prepares Firestore profiles for: {DEFAULT_TEACHERS.map((t) => t.email).join(', ')}. Then run{' '}
          <code>node scripts/import-teachers.js</code> to create Auth logins with random 6-digit passwords.
        </p>
        <button type="button" className="mps-btn" disabled={seedingTeachers} onClick={handleSeedTeachers}>
          {seedingTeachers ? 'Preparing…' : 'Prepare teacher profiles'}
        </button>
      </div>

      <div className="mps-card">
        <h2>{editingSubjectId ? 'Edit subject' : 'Add subject'}</h2>
        <form onSubmit={handleSubject}>
          <div className="mps-grid">
            <div className="mps-field">
              <label>Subject name</label>
              <input value={subjectForm.name} onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })} required />
            </div>
            <div className="mps-field">
              <label>Class (optional)</label>
              <SearchableSelect
                value={subjectForm.className}
                onChange={(v) => setSubjectForm({ ...subjectForm, className: v })}
                options={classOptsEmpty}
                placeholder="All classes if empty…"
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button type="submit" className="mps-btn">
              {editingSubjectId ? 'Update subject' : 'Save subject'}
            </button>
            {editingSubjectId ? (
              <button type="button" className="mps-btn mps-btn-secondary" onClick={handleCancelEditSubject}>
                Cancel
              </button>
            ) : null}
          </div>
        </form>
        {subjects.length > 0 ? (
          <>
            <h3 style={{ marginTop: 20, marginBottom: 8 }}>Subjects ({subjects.length})</h3>
            <table className="mps-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Class</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {subjects.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{s.class ? formatClassLabel(s.class) : 'All classes'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="mps-btn mps-btn-sm mps-btn-secondary" onClick={() => handleEditSubject(s)}>
                        Edit
                      </button>{' '}
                      <button type="button" className="mps-btn mps-btn-sm mps-btn-danger" onClick={() => handleDeleteSubject(s)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
      </div>

      <div className="mps-card">
        <h2>Add exam / test</h2>
        <form onSubmit={handleAssessment}>
          <div className="mps-grid">
            <div className="mps-field">
              <label>Name</label>
              <input value={assessmentForm.name} onChange={(e) => setAssessmentForm({ ...assessmentForm, name: e.target.value })} required />
            </div>
            <div className="mps-field">
              <label>Type</label>
              <select value={assessmentForm.type} onChange={(e) => setAssessmentForm({ ...assessmentForm, type: e.target.value })}>
                <option value="EXAM">Exam</option>
                <option value="TEST">Test</option>
              </select>
            </div>
            <div className="mps-field">
              <label>Class</label>
              <SearchableSelect
                value={assessmentForm.className}
                onChange={(v) => {
                  const valid = new Set(subjectsForClass(subjects, v).map((s) => s.name));
                  setAssessmentForm({
                    ...assessmentForm,
                    className: v,
                    selectedSubjects: (assessmentForm.selectedSubjects || []).filter((n) => valid.has(n)),
                  });
                }}
                options={classOptsAll}
                placeholder="Class or all classes…"
              />
            </div>
            <div className="mps-field mps-field-full">
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <label>Subjects ({assessmentForm.selectedSubjects?.length || 0} selected)</label>
                {classSubjectRows.length > 0 ? (
                  <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="mps-btn mps-btn-secondary mps-btn-sm" onClick={selectAllAssessmentSubjects}>
                      Select all
                    </button>
                    <button type="button" className="mps-btn mps-btn-secondary mps-btn-sm" onClick={clearAssessmentSubjects}>
                      Clear
                    </button>
                  </span>
                ) : null}
              </div>
              {classSubjectRows.length > 0 ? (
                <ul className="mps-subject-checklist">
                  {classSubjectRows.map((s) => {
                    const inputId = `ass-sub-${s.id || s.name}`;
                    const checked = (assessmentForm.selectedSubjects || []).includes(s.name);
                    return (
                      <li key={s.id || s.name}>
                        <input
                          id={inputId}
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAssessmentSubject(s.name)}
                        />
                        <label htmlFor={inputId}>{s.name}</label>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mps-muted" style={{ margin: '8px 0 0' }}>
                  No subjects for this class. Add subjects above first.
                </p>
              )}
            </div>
            <div className="mps-field">
              <label>Max marks</label>
              <input type="number" value={assessmentForm.maxMarks} onChange={(e) => setAssessmentForm({ ...assessmentForm, maxMarks: e.target.value })} />
            </div>
            <div className="mps-field">
              <label>Date</label>
              <input type="date" value={assessmentForm.date} onChange={(e) => setAssessmentForm({ ...assessmentForm, date: e.target.value })} />
            </div>
          </div>
          <button type="submit" className="mps-btn" style={{ marginTop: 12 }}>
            Save exam / test
          </button>
        </form>
      </div>

      <div className="mps-card">
        <h2>Exams &amp; tests ({assessments.length})</h2>
        {loading ? (
          <p>Loading…</p>
        ) : (
          <>
            <input
              type="search"
              className="mps-table-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search name, class, subject…"
            />
            <table className="mps-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Class</th>
                  <th>Subject</th>
                  <th>Max</th>
                  <th>Date</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredAssessments.map((a) => (
                  <tr key={a.id}>
                    <td>{a.type}</td>
                    <td>{a.name}</td>
                    <td>{formatClassLabel(a.class)}</td>
                    <td>{a.subject}</td>
                    <td>{a.maxMarks}</td>
                    <td>{a.date}</td>
                    <td>
                      <button type="button" className="mps-btn mps-btn-sm mps-btn-danger" onClick={() => handleDeleteAssessment(a)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="mps-card">
        <h2>Syllabus import &amp; PDF</h2>
        <p className="mps-muted" style={{ marginTop: 0 }}>
          CSV columns: <code>class,subject,content</code> (content may contain commas).
        </p>
        <div className="mps-field">
          <label htmlFor="syllabus-csv">Import syllabus CSV</label>
          <input id="syllabus-csv" type="file" accept=".csv,text/csv" onChange={handleSyllabusImport} />
        </div>
        <div className="mps-grid" style={{ marginTop: 12, alignItems: 'end' }}>
          <div className="mps-field">
            <label>Download PDF for class</label>
            <SearchableSelect value={pdfClass} onChange={setPdfClass} options={classOpts} placeholder="Class…" />
          </div>
          <button type="button" className="mps-btn mps-btn-secondary" onClick={handlePdf}>
            Download / print syllabus PDF
          </button>
        </div>
        <p className="mps-muted" style={{ marginTop: 12 }}>
          {syllabusRows.length} syllabus document(s) in database.
        </p>
      </div>
    </>
  );
}
